import {
  CaretRight,
  ChartLine,
  Check,
  Command,
  Database,
  MagnifyingGlass,
  PenNib,
  Star,
} from "@phosphor-icons/react"
import { PRODUCTS } from "@/constants/urls"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"
import { MasonryWidget } from "./MasonryWidget"

const mockQueryResult = {
  rows: [
    {
      timestamp: "5/1/2012",
      data_id: "bitcoin",
      metric_id: "active_addresses_daily",
      value: 11443,
    },
    {
      timestamp: "5/2/2012",
      data_id: "bitcoin",
      metric_id: "active_addresses_daily",
      value: 11573,
    },
    {
      timestamp: "5/5/2012",
      data_id: "bitcoin",
      metric_id: "active_addresses_daily",
      value: 12299,
    },
  ],
}

const tableItems = [
  { name: "metrics", count: 2, icon: ChartLine },
  { name: "primitives", count: 2, icon: Database },
]

export function MasonryQueryEditor() {
  return (
    <MasonryWidget
      href={PRODUCTS.STUDIO_ROUTE}
      icon={PenNib}
      productName="Studio"
      widgetName="Dive deeper with Queries"
      description="Write and execute SQL queries against raw blockchain data with AI-powered suggestions and instant results."
      height={500}
      featuredVisual={
        <div
          className="relative border-t border-l border-solid border-border-default rounded-tl-xl flex-1 min-h-0"
          style={{ overflow: "hidden", ...FADE_MASK_STYLE }}
        >
          <div
            className="bg-bg-default overflow-hidden"
            style={{ width: "calc(100% + 150px)" }}
          >
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-border-default bg-bg-default">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-fg-secondary">Queries</span>
                <CaretRight className="w-2.5 h-2.5 text-fg-secondary" />
                <span className="text-fg-default">
                  Bitcoin active_addresses
                </span>
                <Star className="w-3 h-3 text-fg-secondary" />
              </div>
            </div>

            <div className="flex">
              <div className="w-[160px] shrink-0 border-r border-border-default bg-bg-default flex flex-col">
                <div className="h-[36px] px-3 flex items-center border-b border-border-default">
                  <button className="flex items-center gap-1 text-xs text-fg-default font-medium py-1.5 border-b-2 border-fg-default -mb-px">
                    <Database className="w-3 h-3" />
                    Tables
                  </button>
                </div>

                <div className="p-2 flex flex-col gap-2">
                  <div className="flex items-center gap-1.5 px-2 py-1.5 bg-bg-inset border border-border-default rounded-sm">
                    <MagnifyingGlass className="w-3 h-3 text-fg-secondary" />
                    <span className="text-xs text-fg-secondary">Search</span>
                  </div>

                  <div className="flex flex-col rounded-sm overflow-hidden border border-border-default">
                    {tableItems.map((item, idx) => (
                      <button
                        key={item.name}
                        className={`flex items-center gap-1 h-8 px-2 py-1.5 w-full text-left bg-bg-default hover:bg-neutral-100 dark:hover:bg-neutral-800 ${
                          idx < tableItems.length - 1
                            ? "border-b border-border-default"
                            : ""
                        }`}
                      >
                        <CaretRight className="w-3 h-3 text-fg-secondary" />
                        <div className="flex justify-center items-center h-4 w-4 rounded-sm bg-neutral-500/10 dark:bg-neutral-500/30 text-fg-secondary">
                          <item.icon className="w-2.5 h-2.5" />
                        </div>
                        <span className="text-xs text-fg-default flex-1">
                          {item.name}
                        </span>
                        <span className="text-[10px] text-fg-secondary">
                          {item.count}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex-1 flex flex-col min-w-0">
                <div className="m-3 border border-border-default rounded-sm overflow-hidden bg-bg-inset">
                  <div className="flex font-mono text-xs">
                    <div className="py-2 px-2 text-right text-fg-tertiary select-none border-r border-border-default bg-bg-default">
                      <div>1</div>
                      <div>2</div>
                      <div>3</div>
                    </div>
                    <div className="py-2 px-3 flex-1">
                      <div>
                        <span className="text-[#C586C0]">select</span>
                        <span className="text-fg-default"> *</span>
                      </div>
                      <div>
                        <span className="text-[#C586C0]">from</span>
                        <span className="text-fg-default"> metrics</span>
                      </div>
                      <div>
                        <span className="text-[#C586C0]">where</span>
                        <span className="text-fg-default"> data_id = </span>
                        <span className="text-[#CE9178]">
                          &apos;bitcoin&apos;
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between px-3 pb-2 text-xs">
                  <div className="flex items-center gap-1 text-fg-secondary">
                    <span className="px-1 py-0.5 bg-bg-inset border border-border-default rounded-sm text-[10px] font-medium">
                      <Command className="w-2.5 h-2.5 inline" />I
                    </span>
                    <span>AI suggestions</span>
                  </div>
                  <button className="px-3 py-1 bg-blue-600 text-white rounded-sm text-xs font-medium hover:bg-blue-700">
                    Run query
                  </button>
                </div>

                <div className="flex items-center gap-2 px-3 py-2 border-t border-border-default text-xs">
                  <Check className="w-3 h-3" style={{ color: "#1AFFAB" }} />
                  <span className="text-fg-default font-medium">Success</span>
                  <span className="text-fg-secondary">21 days ago</span>
                </div>

                <div className="border-t border-border-default">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border-default bg-bg-default">
                        <th className="px-3 py-1.5 text-left font-normal text-fg-default">
                          timestamp
                        </th>
                        <th className="px-3 py-1.5 text-left font-normal text-fg-default">
                          data_id
                        </th>
                        <th className="px-3 py-1.5 text-left font-normal text-fg-default">
                          value
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {mockQueryResult.rows.map((row, idx) => (
                        <tr
                          key={idx}
                          className="border-b border-border-default"
                        >
                          <td className="px-3 py-1.5 text-fg-default">
                            {row.timestamp}
                          </td>
                          <td className="px-3 py-1.5 text-fg-default">
                            {row.data_id}
                          </td>
                          <td className="px-3 py-1.5 text-fg-default tabular-nums">
                            {row.value.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    />
  )
}
